import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import Header from "../../../util/component/Header";
import axios from "axios";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import CustomSnackbar from "../../../util/component/CustomSnackbar";
import CustomLoader from "../../../util/component/CustomLoader";
import {useNavigate} from "react-router-dom";
import {useUserActions} from "../service/user.actions";

type Props = {
    value: string[];
    valueLength: number;
    onChange: (index: number, value: string) => void;
    handleEnter: () => void;
    isDisabled: boolean;
};

function OtpInput({value, valueLength, onChange, handleEnter, isDisabled}: Props) {
    const inputRefs = useRef([] as any[]);

    useEffect(() => {
        if (inputRefs.current.length === 5) {
            inputRefs.current[0].focus();
        }
    }, [inputRefs]);
    const handleKeyUp = (event: any, index: number) => {
        if (event.key === 'Backspace' && index > 0) {
            const prevIndex = index - 1;
            inputRefs.current[prevIndex].focus();
            inputRefs.current[prevIndex].select();
        } else if (event.key === 'ArrowLeft' && index > 0) {
            const prevIndex = index - 1;
            inputRefs.current[prevIndex].focus();
            inputRefs.current[prevIndex].select();
        } else if (event.key === 'ArrowRight' && index < inputRefs.current.length - 1) {
            const nextIndex = index + 1;
            inputRefs.current[nextIndex].focus();
            inputRefs.current[nextIndex].select();
        } else if (event.key === 'Enter' && !isDisabled) {
            handleEnter();
        } else if (event.key !== 'Shift' && event.key !== 'Backspace' && event.key !== 'Enter'
            && event.key !== 'Alt'  && event.key !== 'Meta'  && event.key !== 'Control'
            && index < inputRefs.current.length - 1) {
            const nextIndex = index + 1
            inputRefs.current[nextIndex].focus();
            inputRefs.current[nextIndex].select();
        }
    };

    return (
        <Grid container spacing={1} justifyContent={'center'}>
            {[1, 2, 3, 4, 5].map((digit, idx) => (
                <Grid item key={idx}>
                    <TextField
                        sx={{
                            width: '50px'
                        }}
                        type="text"
                        inputMode="text"
                        variant={"outlined"}
                        inputProps={{
                            maxLength: valueLength,
                            pattern: '\\w{1}',
                            onKeyUp: (e) => {
                                return handleKeyUp(e, idx);
                            }
                        }}
                        value={value[idx]}
                        onChange={(e) => onChange(idx, e.target.value)}
                        inputRef={(ref) => (inputRefs.current[idx] = ref)}
                    />
                </Grid>
            ))}
        </Grid>
    );
}

export interface IVerifyCode {
    status: string;
}

const VerifyCode = ({status}:IVerifyCode) => {
    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['operator']);
    const [otp, setOtp] = useState(['', '', '', '', '']);
    const [disabled, setDisabled] = useState(true);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const userActions = useUserActions();

    useEffect(() => {
        setDisabled(otp.some((digit) => digit.length === 0));
    }, [otp]);

    const onChange = (index: number, value: string) => {
        setOtp((otp) => {
            const otpCopy = [...otp];
            otpCopy[index] = value;
            return otpCopy;
        })
    };
    const handleClose = () => {
    };

    const handleVerify = () => {
        setIsLoading(true);
        axios.post('/code/verify', {
            code: otp.join('')
        }).then((response) => {
            navigate("/");
        }).catch((error) => {
            console.log(error);
            if (error.response.status === 400) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.timeOut'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            } else if (error.response.status === 403) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.codeNotValid'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            }
        }).finally(() => {setIsLoading(false)});
    }

    const handleUpdate = () => {
        setIsLoading(true);
        axios.put('/updatecode', {
            code: otp.join('')
        }).then((response) => {
            userActions.getUser(globalData?.user?.username);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:success.updateOperation'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }).catch((error) => {
            console.log(error);
            if (error.response.status === 400) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.authCodeMustBeDifferent'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('translation:error.updateOperation'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            }
        }).finally(() => {setIsLoading(false)});
    }

    const handleForgottenCode = () => {
        setIsLoading(true);
        axios.post('/code').then((response) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.success.codeSent'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }).catch((error) => {
            console.log(error);
            if (error.response.status === 400) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.timeOut'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        },
                    },
                });
            }
        }).finally(() => {setIsLoading(false)});;
    }

    return (
        <>
            <Header menuIsOpen={false} setMenuIsOpen={null}/>
            <Dialog
                open={true}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
                disableEscapeKeyDown={true}
            >
                <DialogTitle id="responsive-dialog-title" sx={{display: 'flex', justifyContent: 'center'}}>
                    {status === 'verify' ? t('label.enterAuthCode') : t('label.enterNewCustomAuthCode')}
                </DialogTitle>
                <DialogContent>
                    <OtpInput value={otp} valueLength={1} onChange={onChange} handleEnter={status === 'verify' ? handleVerify : handleUpdate} isDisabled={disabled}/>
                </DialogContent>
                <DialogActions style={{justifyContent: status === 'verify' ? "space-between" : "end"}}>
                    {status === 'verify' && <Button onClick={handleForgottenCode} variant={'outlined'}>
                        {t('label.forgottenCode')}
                    </Button>}
                    <Button onClick={status === 'verify' ? handleVerify : handleUpdate} disabled={disabled}>
                        {t('label.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomSnackbar/>
            <CustomLoader loading={isLoading}/>
        </>
    );
};

export default VerifyCode;
